import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, ShieldOff, ShieldCheck, Loader2, Search, Info, RefreshCw, Ban } from 'lucide-react';
import { BanUserDialogs } from '@/components/BanUserDialogs';
import { UserAvatar } from '@/components/UserAvatar';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { ageFromDob, DobGraceBadge } from '@/components/PassengerAge';

interface Passenger {
  id: string;
  full_name: string;
  phone: string | null;
  phone_number?: string | null;
  avatar_url: string | null;
  profile_picture_url: string | null;
  total_rides: number;
  completed_rides: number;
  last_ride_at: string | null;
  suspended_at: string | null;
  suspension_reason: string | null;
  banned_at?: string | null;
  ban_reason?: string | null;
  pending_ban_at?: string | null;
  pending_ban_reason?: string | null;
  // Legacy field fallback
  is_suspended?: boolean;
  created_at: string;
  // DOB + grace
  date_of_birth: string | null;
  dob_grace_until: string | null;
}

function isSuspended(p: Passenger): boolean {
  return p.suspended_at != null || p.is_suspended === true;
}

function isBanned(p: Passenger): boolean {
  return p.banned_at != null;
}

function isBanPending(p: Passenger): boolean {
  return p.banned_at == null && p.pending_ban_at != null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function Passengers() {
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Drawer state
  const [selectedPassenger, setSelectedPassenger] = useState<Passenger | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Suspend dialog
  const [suspendDialog, setSuspendDialog] = useState<Passenger | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reinstate dialog
  const [reinstateDialog, setReinstateDialog] = useState<Passenger | null>(null);

  // Permanent ban dialogs
  const [banDialog, setBanDialog] = useState<Passenger | null>(null);
  const [unbanDialog, setUnbanDialog] = useState<Passenger | null>(null);

  useEffect(() => {
    fetchPassengers();
  }, []);

  const fetchPassengers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_passengers_admin');
      if (error) throw error;
      const response = data as any;
      const list = response?.passengers ?? (Array.isArray(data) ? data : []);
      setPassengers(list);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load passengers');
    } finally {
      setLoading(false);
    }
  };

  // ─── Suspend action ────────────────────────────────────────────────────────

  const openSuspendDialog = (passenger: Passenger) => {
    setSuspendDialog(passenger);
    setSuspendReason('');
    setDrawerOpen(false);
  };

  const handleSuspend = async () => {
    if (!suspendDialog) return;
    if (suspendReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('set_user_suspension', {
        p_user_id: suspendDialog.id,
        p_suspended: true,
        p_reason: suspendReason.trim(),
      });
      if (error) throw error;

      // Optimistic update
      setPassengers((prev) =>
        prev.map((p) =>
          p.id === suspendDialog.id
            ? { ...p, suspended_at: new Date().toISOString(), suspension_reason: suspendReason.trim(), is_suspended: true }
            : p
        )
      );
      if (selectedPassenger?.id === suspendDialog.id) {
        setSelectedPassenger((prev) => prev ? { ...prev, suspended_at: new Date().toISOString(), suspension_reason: suspendReason.trim(), is_suspended: true } : prev);
      }

      toast.success('Passenger suspended');
      setSuspendDialog(null);
      setSuspendReason('');

      // Confirm from backend
      fetchPassengers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to suspend passenger');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Reinstate action ──────────────────────────────────────────────────────

  const openReinstateDialog = (passenger: Passenger) => {
    setReinstateDialog(passenger);
    setDrawerOpen(false);
  };

  const handleReinstate = async () => {
    if (!reinstateDialog) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('set_user_suspension', {
        p_user_id: reinstateDialog.id,
        p_suspended: false,
        p_reason: null,
      });
      if (error) throw error;

      // Optimistic update
      setPassengers((prev) =>
        prev.map((p) =>
          p.id === reinstateDialog.id
            ? { ...p, suspended_at: null, suspension_reason: null, is_suspended: false }
            : p
        )
      );
      if (selectedPassenger?.id === reinstateDialog.id) {
        setSelectedPassenger((prev) => prev ? { ...prev, suspended_at: null, suspension_reason: null, is_suspended: false } : prev);
      }

      toast.success('Passenger reinstated');
      setReinstateDialog(null);

      // Confirm from backend
      fetchPassengers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reinstate passenger');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Drawer ────────────────────────────────────────────────────────────────

  const openDrawer = (passenger: Passenger) => {
    setSelectedPassenger(passenger);
    setDrawerOpen(true);
  };

  // ─── Filter ────────────────────────────────────────────────────────────────

  const filtered = search
    ? passengers.filter((p) =>
        p.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.phone?.includes(search)
      )
    : passengers;

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Passengers</h1>
            <p className="text-sm text-gray-400 mt-1">Passenger roster and suspension controls</p>
          </div>
          <div className="flex items-center gap-2">
            {/*
              Manual refresh. The page doesn't subscribe to realtime,
              so ride counts / DOB only update on (re)fetch. Cheaper
              and more transparent than a polling interval — operators
              click when they expect data to have changed.
            */}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPassengers}
              disabled={loading}
              className="text-gray-400 hover:text-white"
              title="Refresh passenger roster"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Badge variant="outline" className="text-gray-400 border-gray-700">
              {passengers.length} total
            </Badge>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-500"
          />
        </div>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">{search ? 'No passengers match your search' : 'No passengers found'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Passenger</TableHead>
                    <TableHead className="text-gray-400">Contact</TableHead>
                    <TableHead className="text-gray-400 text-center">Age</TableHead>
                    <TableHead className="text-gray-400 text-center">Total Rides</TableHead>
                    <TableHead className="text-gray-400 text-center">Completed</TableHead>
                    <TableHead className="text-gray-400">Last Ride</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow
                      key={p.id}
                      className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => openDrawer(p)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            size="md"
                            name={p.full_name}
                            src={p.profile_picture_url || p.avatar_url}
                            ring={isSuspended(p) ? 'red' : 'none'}
                          />
                          <span className="text-white font-medium">{p.full_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <ContactRow label="Phone" value={formatDominicaPhone(p.phone_number ?? p.phone)} callable />
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const age = ageFromDob(p.date_of_birth);
                          if (age !== null) return <span className="text-gray-300 font-medium">{age}</span>;
                          return <DobGraceBadge graceUntil={p.dob_grace_until} compact />;
                        })()}
                      </TableCell>
                      <TableCell className="text-gray-300 text-center">{p.total_rides ?? 0}</TableCell>
                      <TableCell className="text-gray-300 text-center">{p.completed_rides ?? 0}</TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {p.last_ride_at ? timeAgo(p.last_ride_at) : 'Never'}
                      </TableCell>
                      <TableCell>
                        {isBanned(p) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-red-500/10 text-red-400 border-red-500/20 cursor-help">
                                Banned
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                {p.banned_at && `Banned ${timeAgo(p.banned_at)}`}
                                {p.ban_reason && `: ${p.ban_reason}`}
                                {' · Identities blocklisted (no new accounts)'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : isBanPending(p) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 cursor-help">
                                Ban pending
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                Ban scheduled {p.pending_ban_at && timeAgo(p.pending_ban_at)}. Waiting for their
                                active trip to end, then it fires automatically.
                                {p.pending_ban_reason && ` Reason: ${p.pending_ban_reason}`}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : isSuspended(p) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-help">
                                Suspended
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                {p.suspended_at && `Suspended ${timeAgo(p.suspended_at)}`}
                                {p.suspension_reason && `: ${p.suspension_reason}`}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {isBanned(p) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => { setUnbanDialog(p); setDrawerOpen(false); }}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Lift ban
                          </Button>
                        ) : isBanPending(p) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            onClick={() => { setUnbanDialog(p); setDrawerOpen(false); }}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Cancel ban
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {isSuspended(p) ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                onClick={() => openReinstateDialog(p)}
                              >
                                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Reinstate
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                onClick={() => openSuspendDialog(p)}
                              >
                                <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suspend
                              </Button>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => { setBanDialog(p); setDrawerOpen(false); }}
                                >
                                  <Ban className="w-3.5 h-3.5 mr-1" /> Ban
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                                <p className="text-xs">Permanent ban. Blocks new accounts on the same phone, email, license, or plate. Reversible.</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="right" className="bg-[#111827] border-gray-800 text-white w-[400px] sm:max-w-[400px]">
            <SheetHeader>
              <SheetTitle className="text-white">Passenger Details</SheetTitle>
            </SheetHeader>

            {selectedPassenger && (
              <div className="mt-6 space-y-6">
                {/* Passenger info */}
                <div className="flex items-center gap-4">
                  <UserAvatar
                    size="xl"
                    name={selectedPassenger.full_name}
                    src={selectedPassenger.profile_picture_url || selectedPassenger.avatar_url}
                    ring={isSuspended(selectedPassenger) ? 'red' : 'none'}
                  />
                  <div>
                    <p className="text-lg font-semibold text-white">{selectedPassenger.full_name}</p>
                  </div>
                </div>

                {/* Contact info */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 space-y-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Personal Info</p>
                  <ContactRow label="Phone" value={formatDominicaPhone(selectedPassenger.phone_number ?? selectedPassenger.phone)} callable />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Date of birth</span>
                    {selectedPassenger.date_of_birth ? (
                      <span className="text-sm text-gray-200">
                        {new Date(selectedPassenger.date_of_birth).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        <span className="text-gray-500 ml-1.5">({ageFromDob(selectedPassenger.date_of_birth)} yrs)</span>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">
                        Not provided
                        {selectedPassenger.dob_grace_until && (
                          <DobGraceBadge graceUntil={selectedPassenger.dob_grace_until} />
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Rides</p>
                    <p className="text-xl font-bold text-white mt-1">{selectedPassenger.total_rides ?? 0}</p>
                  </div>
                  <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Completed</p>
                    <p className="text-xl font-bold text-emerald-400 mt-1">{selectedPassenger.completed_rides ?? 0}</p>
                  </div>
                </div>

                {/* Status card */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status</p>
                  {isBanned(selectedPassenger) ? (
                    <div>
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-sm px-3 py-1">
                        Permanently Banned
                      </Badge>
                      {selectedPassenger.banned_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Since {timeAgo(selectedPassenger.banned_at)} · Identities blocklisted
                        </p>
                      )}
                      {selectedPassenger.ban_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedPassenger.ban_reason}"
                        </p>
                      )}
                    </div>
                  ) : isBanPending(selectedPassenger) ? (
                    <div>
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-sm px-3 py-1">
                        Ban Pending
                      </Badge>
                      <p className="text-xs text-gray-500 mt-2">
                        Fires automatically the moment their active trip ends.
                      </p>
                      {selectedPassenger.pending_ban_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedPassenger.pending_ban_reason}"
                        </p>
                      )}
                    </div>
                  ) : isSuspended(selectedPassenger) ? (
                    <div>
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-sm px-3 py-1">
                        Suspended
                      </Badge>
                      {selectedPassenger.suspended_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Since {timeAgo(selectedPassenger.suspended_at)}
                        </p>
                      )}
                      {selectedPassenger.suspension_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedPassenger.suspension_reason}"
                        </p>
                      )}
                    </div>
                  ) : (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-sm px-3 py-1">
                      Active
                    </Badge>
                  )}
                </div>

                {/* Action buttons */}
                <div className="pt-2 space-y-2">
                  {isBanned(selectedPassenger) ? (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                      onClick={() => { setUnbanDialog(selectedPassenger); setDrawerOpen(false); }}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" /> Lift Ban
                    </Button>
                  ) : isBanPending(selectedPassenger) ? (
                    <Button
                      className="w-full bg-orange-600 hover:bg-orange-500 text-white"
                      onClick={() => { setUnbanDialog(selectedPassenger); setDrawerOpen(false); }}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" /> Cancel Scheduled Ban
                    </Button>
                  ) : (
                    <>
                      {isSuspended(selectedPassenger) ? (
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                          onClick={() => openReinstateDialog(selectedPassenger)}
                        >
                          <ShieldCheck className="w-4 h-4 mr-2" /> Reinstate Passenger
                        </Button>
                      ) : (
                        <Button
                          className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                          onClick={() => openSuspendDialog(selectedPassenger)}
                        >
                          <ShieldOff className="w-4 h-4 mr-2" /> Suspend Passenger
                        </Button>
                      )}
                      <Button
                        className="w-full bg-red-700 hover:bg-red-600 text-white"
                        onClick={() => { setBanDialog(selectedPassenger); setDrawerOpen(false); }}
                      >
                        <Ban className="w-4 h-4 mr-2" /> Ban Permanently
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Suspend Dialog */}
        <Dialog open={!!suspendDialog} onOpenChange={(open) => { if (!open) setSuspendDialog(null); }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Suspend Passenger</DialogTitle>
              <DialogDescription className="text-gray-400">
                {suspendDialog?.full_name} will be immediately blocked from creating new ride requests on the mobile app.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-gray-300">
                  Reason <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  placeholder="e.g. Abusive language, fraud, safety concern"
                />
                {suspendReason.length > 0 && suspendReason.trim().length < 3 && (
                  <p className="text-xs text-red-400">Reason must be at least 3 characters</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSuspendDialog(null)} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={handleSuspend}
                disabled={submitting || suspendReason.trim().length < 3}
                className="bg-red-600 hover:bg-red-500 text-white"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Suspend
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reinstate Dialog */}
        <Dialog open={!!reinstateDialog} onOpenChange={(open) => { if (!open) setReinstateDialog(null); }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Reinstate Passenger</DialogTitle>
              <DialogDescription className="text-gray-400">
                Reinstate {reinstateDialog?.full_name}? They will be able to create ride requests again.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-300">
                  The passenger will be able to request rides immediately after reinstatement.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setReinstateDialog(null)} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={handleReinstate}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Reinstate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent ban / lift ban */}
        <BanUserDialogs
          roleLabel="passenger"
          banTarget={banDialog}
          unbanTarget={unbanDialog}
          onCloseBan={() => setBanDialog(null)}
          onCloseUnban={() => setUnbanDialog(null)}
          onDone={fetchPassengers}
        />
      </div>
    </TooltipProvider>
  );
}
